import getFlattenedFiles from '@picsart/cloud-storage/api/getFlattenedFiles';
import updateFileMeta from '@picsart/cloud-storage/api/updateFileMeta';
import uploadFileToSystemFolder from '@picsart/cloud-storage/api/uploadFileToSystemFolder';
import type { IFile } from '@picsart/cloud-storage/types/core/types/files/file';

import { ErrorMessages } from '@/consts/errors';
import {
  getCloudStorageHeaders,
  withCloudStorageErrorHandling,
  withCloudStorageWriteHandling,
} from './utils';

/**
 * A string list persisted on one hidden file in the Drive tool folder — the
 * mechanics behind saved presets and liked presets. Values live in an attribute,
 * not file content, because the drive's content-update endpoint rejects
 * `multipart/form-data` (415); attributes are updated with a JSON PATCH, exactly
 * like `userReaction` in `setFileReaction`. Custom attributes must use the
 * drive's `namespace~name` format.
 */
export interface HiddenListFile {
  values: string[];
  /** Drive file id, or null when the file hasn't been created yet. */
  fileId: string | null;
}

interface FileTarget {
  toolId: string;
  fileName: string;
  attribute: string;
}

/**
 * The create resolves a workflow envelope, not the `IFile` its type promises —
 * the new id is at `response.result.uid`. Reading the declared shape typechecks
 * and returns undefined, so a successful create reads as a failed write.
 */
const toCreatedFileId = (response?: IFile): string | null => {
  const envelope = response as { result?: { uid?: string } } | undefined;

  return envelope?.result?.uid ?? response?.uid ?? null;
};

const parseValues = (raw: unknown): string[] => {
  if (typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string')
      : [];
  } catch {
    return [];
  }
};

/**
 * Reads the list from the hidden file's attributes. Returns the values and the
 * file id (null if it doesn't exist yet), or null on error.
 */
export const readHiddenListFile = async ({
  toolId,
  fileName,
  attribute,
}: FileTarget): Promise<HiddenListFile | null> => {
  const result = await withCloudStorageErrorHandling({
    operation: () =>
      getFlattenedFiles({
        headers: getCloudStorageHeaders(),
        params: { limit: '100', offset: '0' },
        onlyHiddenFiles: true,
        attributes: { tool: toolId },
      }),
    errorMessage: ErrorMessages.loadDriveFiles,
  });

  if (!result) return null;

  const file = ((result.response ?? []) as IFile[]).find(
    (item) => item?.name === fileName
  );

  if (!file?.uid) return { values: [], fileId: null };

  // Custom attributes round-trip in the listing at runtime (like
  // `userReaction`), even though they're absent from the closed IFile type.
  const attributes = file.attributes as Record<string, unknown> | undefined;

  return {
    values: parseValues(attributes?.[attribute]),
    fileId: file.uid,
  };
};

/**
 * Persists the list. Updates the existing hidden file's attributes, or creates
 * the file (then patches it) on first write. Returns the (possibly new) file id,
 * or null on error.
 */
export const writeHiddenListFile = async ({
  toolId,
  fileName,
  attribute,
  values,
  fileId,
}: FileTarget & {
  values: string[];
  fileId: string | null;
}): Promise<{ fileId: string | null } | null> => {
  let targetId = fileId;

  if (!targetId) {
    // First write: create the hidden placeholder file.
    const created = await withCloudStorageWriteHandling({
      operation: () =>
        uploadFileToSystemFolder({
          headers: getCloudStorageHeaders(),
          file: new File(['[]'], fileName, { type: 'application/json' }),
          toolId,
          isFileHidden: true,
        }),
      errorMessage: ErrorMessages.saveDriveFile,
    });

    targetId = toCreatedFileId(created?.response);
    if (!targetId) return null;
  }

  const patched = await withCloudStorageWriteHandling({
    operation: () =>
      updateFileMeta({
        headers: getCloudStorageHeaders(),
        fileId: targetId,
        // `FileAttributesMap` is keyed by `namespace~name`, which is the shape
        // the drive expects — no cast needed since cloud-storage v16.
        attributes: { [attribute]: JSON.stringify(values) },
      }),
    errorMessage: ErrorMessages.saveDriveFile,
  });

  if (!patched) return null;

  return { fileId: targetId };
};
